import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles
} from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import WarningIcon from '@material-ui/icons/ErrorOutline'
import Dayjs from 'dayjs'
import React from 'react'

import {
  createCashDeposit,
  fetchCashLedger,
  saveCashReceived
} from 'api/cashRegister'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'

import NoteCounter, {
  NoteTally,
  anyCounted,
  noteTotal,
  toEditable,
  toPayload
} from './NoteCounter'

// Centre view, Variant A (user 2026-08-26): ONE ledger — every cash/cheque
// collection is one row forever. Undeposited rows float to the top with a live
// age chip; tick rows → Record where it went → the chip turns green in place and the
// challan link appears. Collections are recorded by Orders → Collect, never
// here — this screen only gets money out of the drawer.
//
// ⭐ Booked ≠ Received (user, 2026-09-01). The invoice value is what was
// billed; the cash that actually arrived is often less — a course changed
// mid-term, a leg was paid by QR, wallet credit was knocked off. Received is
// typed straight into the row (Option A), and the moment it differs from
// Booked the reason dropdown appears and has to be filled. Reason labels come
// from the server, never from a copy kept here.
//
// ⭐ Notes, not just amounts (user, 2026-09-02). The HQ sheet has counted every
// envelope by denomination since June 2025, because a bank pay-in slip asks
// how many five-hundreds and no total can answer that. The counter lives in
// one dialog opened by clicking the Received figure — count the bundle and the
// total fills itself, or type the amount and count afterwards. It replaced
// three inline editors squeezed into table cells (user, 09-02: "this doesn't
// look nice"): a money column shows money, and the counting needs more room
// than a cell has. Counting is required before cash can be settled anywhere
// except the pre-register backlog, which was banked before this screen existed
// and cannot be counted now.
const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  tallyStrip: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2)
  },
  tallyHead: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1)
  },
  kpis: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2)
  },
  kpi: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: theme.spacing(1.5)
  },
  kpiHot: { borderColor: '#f57c00', background: '#fff8f2' },
  kpiValue: { fontWeight: 700, fontSize: 20 },
  kpiLabel: { color: '#777', fontSize: 12 },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minHeight: 40,
    marginBottom: theme.spacing(1)
  },
  amountCell: { fontVariantNumeric: 'tabular-nums' },
  bookedCell: { fontVariantNumeric: 'tabular-nums', color: '#8a8175' },
  // An untouched row shows the booked value as a stand-in. Greyed, because
  // nobody has confirmed it is the cash that actually arrived.
  assumed: { color: '#9e9e9e' },
  // The editor lives INSIDE the 'Why different' cell and must stay there — a
  // fullWidth field with a minWidth wider than the column pushes the table out
  // and paints over Mode and Collected by.
  placeholder: { color: '#9e9e9e' },
  warnHint: { color: '#c0453f', marginLeft: 2 },
  editHint: { opacity: 0, transition: 'opacity .12s' },
  editable: { cursor: 'pointer', '&:hover $editHint': { opacity: 0.55 } }
}))

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`
const signedInr = (n) =>
  `${Number(n) > 0 ? '+' : Number(n) < 0 ? '−' : ''}${inr(
    Math.abs(Number(n) || 0)
  )}`

// What happened to the cash, per settlement route. The destination itself
// (which bank, which person) always renders underneath — a status that reads
// "Banked" and stops there tells nobody anything.
const SETTLED_LABEL = {
  bank: 'Deposited',
  hq: 'Sent to HQ',
  handover: 'Handed over',
  pre_register: 'Banked earlier'
}

const CHIP_STYLE = {
  in_hand: { background: '#fff8e1', color: '#8d6e00' },
  in_hand_old: { background: '#ffebee', color: '#c62828' },
  deposited: { background: '#e8f5e9', color: '#2e7d32' },
  matches: { background: '#eceff1', color: '#546e7a' },
  variance: { background: '#fdece7', color: '#c0453f' }
}

// Fallbacks for the local-preview path below only. On any real deploy these
// come from the ledger response.
// Only reached when the API is unreachable and the dev sample renders.
const FALLBACK_DENOMINATIONS = [500, 200, 100, 50, 20, 10, 5, 2, 1]
const FALLBACK_ROUTES = [
  {
    value: 'bank',
    label: 'Deposited it in the bank',
    needs_challan: true,
    needs_count: true
  },
  {
    value: 'hq',
    label: 'Sent it to HQ Finance',
    needs_challan: false,
    needs_count: true
  },
  {
    value: 'handover',
    label: 'Handed it to a colleague at the centre',
    needs_challan: false,
    needs_count: true
  },
  {
    value: 'pre_register',
    label: 'Already banked, before this page existed',
    needs_challan: false,
    needs_count: false
  }
]

// LOCAL PREVIEW ONLY (vite dev server; never true in any build): until the
// cash_register endpoints reach the dev API, synthesize a ledger on fetch
// failure so the screen can be reviewed on localhost. Same pattern as the
// CenterTeam cycle-window fallback.
const SAMPLE_LEDGER = {
  start_date: '2026-08-01',
  escalation_days: 3,
  is_finance: true,
  reasons: [
    {
      value: 'other_mode',
      label: 'Part paid by UPI / QR / POS / bank',
      sub_label: 'Paid by the other mode',
      keeps_balance: false
    },
    {
      value: 'plan_changed',
      label: 'Course or plan changed — differential only',
      sub_label: '',
      keeps_balance: false
    },
    {
      value: 'discount',
      label: 'Discount applied',
      sub_label: '',
      keeps_balance: false
    },
    {
      value: 'credit_adjusted',
      label: 'Coins / wallet credit adjusted',
      sub_label: 'Credit used',
      keeps_balance: false
    },
    {
      value: 'paid_extra',
      label: 'Parent paid extra — change to return',
      sub_label: 'To return to the parent',
      keeps_balance: false
    },
    {
      value: 'part_payment',
      label: 'Part payment — balance still due',
      sub_label: 'Balance still expected',
      keeps_balance: true
    },
    {
      value: 'rounding',
      label: 'Rounding / small change',
      sub_label: '',
      keeps_balance: false
    },
    {
      value: 'other',
      label: 'Something else',
      sub_label: '',
      keeps_balance: false
    }
  ],
  routes: FALLBACK_ROUTES,
  across_centres: false,
  denominations: FALLBACK_DENOMINATIONS,
  note_tally: {
    rows: [
      { denomination: 500, count: 121, value: 60500 },
      { denomination: 200, count: 2, value: 400 },
      { denomination: 100, count: 4, value: 400 },
      { denomination: 50, count: 1, value: 50 },
      { denomination: 20, count: 3, value: 60 },
      { denomination: 10, count: 1, value: 10 },
      { denomination: 5, count: 0, value: 0 },
      { denomination: 2, count: 0, value: 0 },
      { denomination: 1, count: 0, value: 0 }
    ],
    total: 61420
  },
  kpis: {
    in_hand_amount: 46420,
    in_hand_count: 4,
    oldest_days: 6,
    deposited_month_amount: 142300,
    expected_balance_amount: 500,
    uncounted_count: 1
  },
  results: [
    {
      uuid: 's1',
      collected_on: '2026-08-26',
      payer: 'Ridhima S · After School',
      parent_phone: '+91 98300 11223',
      booked_amount: 18540,
      amount_received: 18540,
      amount: 18540,
      variance: 0,
      expected_balance: 0,
      mode: 'cash',
      collected_by: 'Meenakshi Ghosh',
      status: 'in_hand',
      age_days: 0,
      recorded: true,
      reason: '',
      reason_label: '',
      note: '',
      finance_edited: false,
      recorded_by: 'Meenakshi Ghosh',
      can_edit_received: true,
      note_count: {
        counted: true,
        counts: {
          500: 37,
          200: 0,
          100: 0,
          50: 0,
          20: 2,
          10: 0,
          5: 0,
          2: 0,
          1: 0
        },
        total: 18540
      },
      hq_receipt: null
    },
    {
      uuid: 's2',
      collected_on: '2026-08-25',
      payer: 'Aarav B · Preschool',
      parent_phone: '+91 98450 44556',
      booked_amount: 164967,
      amount_received: 30000,
      amount: 30000,
      variance: -134967,
      expected_balance: 0,
      mode: 'cash',
      collected_by: 'Meenakshi Ghosh',
      status: 'in_hand',
      age_days: 1,
      recorded: true,
      reason: 'plan_changed',
      reason_label: 'Course or plan changed — differential only',
      note: 'Preschool timing extension',
      finance_edited: false,
      recorded_by: 'Meenakshi Ghosh',
      can_edit_received: true,
      note_count: {
        counted: true,
        counts: {
          500: 60,
          200: 0,
          100: 0,
          50: 0,
          20: 0,
          10: 0,
          5: 0,
          2: 0,
          1: 0
        },
        total: 30000
      },
      hq_receipt: null
    },
    {
      uuid: 's3',
      collected_on: '2026-08-22',
      payer: 'Zoya K · After School',
      parent_phone: '+91 99020 77889',
      booked_amount: 12880,
      amount_received: 12380,
      amount: 12380,
      variance: -500,
      expected_balance: 500,
      mode: 'cheque',
      collected_by: 'Swagata Maiti',
      status: 'in_hand',
      age_days: 4,
      recorded: true,
      reason: 'part_payment',
      reason_label: 'Part payment — balance still due',
      note: '500 yet to be paid',
      finance_edited: false,
      recorded_by: 'Swagata Maiti',
      can_edit_received: true,
      note_count: {
        counted: true,
        counts: {
          500: 24,
          200: 1,
          100: 1,
          50: 1,
          20: 1,
          10: 1,
          5: 0,
          2: 0,
          1: 0
        },
        total: 12380
      },
      hq_receipt: null
    },
    {
      uuid: 's4',
      collected_on: '2026-08-20',
      payer: 'Vihaan R · Summer Camp',
      parent_phone: '+91 96860 33445',
      booked_amount: 12000,
      amount_received: 12000,
      amount: 12000,
      variance: 0,
      expected_balance: 0,
      mode: 'cash',
      collected_by: 'Sanhita Choudhury',
      status: 'in_hand',
      age_days: 6,
      recorded: false,
      reason: '',
      reason_label: '',
      note: '',
      finance_edited: false,
      recorded_by: '',
      can_edit_received: true,
      note_count: {
        counted: false,
        counts: {
          500: 0,
          200: 0,
          100: 0,
          50: 0,
          20: 0,
          10: 0,
          5: 0,
          2: 0,
          1: 0
        },
        total: 0
      },
      hq_receipt: null
    },
    {
      uuid: 's5',
      collected_on: '2026-08-14',
      payer: 'Myra D · After School',
      parent_phone: '+91 90080 99001',
      booked_amount: 21300,
      amount_received: 21300,
      amount: 21300,
      variance: 0,
      expected_balance: 0,
      mode: 'cash',
      collected_by: 'Sanhita Choudhury',
      status: 'deposited',
      age_days: 0,
      recorded: true,
      reason: '',
      reason_label: '',
      note: '',
      finance_edited: false,
      recorded_by: 'Sanhita Choudhury',
      can_edit_received: true,
      note_count: {
        counted: true,
        counts: {
          500: 42,
          200: 1,
          100: 1,
          50: 0,
          20: 0,
          10: 0,
          5: 0,
          2: 0,
          1: 0
        },
        total: 21300
      },
      hq_receipt: null,
      deposit: {
        uuid: 'd1',
        route: 'bank',
        destination: 'HDFC Indiranagar',
        bank: 'HDFC Indiranagar',
        handed_to: '',
        deposited_on: '2026-08-16',
        deposited_by: 'Sanhita Choudhury',
        challans: [{ uuid: 'c1', name: 'slip.jpg', url: '#' }]
      }
    },
    {
      uuid: 's6',
      collected_on: '2026-08-11',
      payer: 'Kabir J · After School',
      parent_phone: '+91 97400 22110',
      booked_amount: 121000,
      amount_received: 121000,
      amount: 121000,
      variance: 0,
      expected_balance: 0,
      mode: 'cash',
      collected_by: 'Meenakshi Ghosh',
      status: 'deposited',
      age_days: 0,
      recorded: true,
      reason: '',
      reason_label: '',
      note: '',
      finance_edited: false,
      recorded_by: 'Meenakshi Ghosh',
      can_edit_received: true,
      note_count: {
        counted: true,
        counts: {
          500: 242,
          200: 0,
          100: 0,
          50: 0,
          20: 0,
          10: 0,
          5: 0,
          2: 0,
          1: 0
        },
        total: 121000
      },
      hq_receipt: null,
      deposit: {
        uuid: 'd2',
        route: 'handover',
        destination: 'Handed to Nehasri',
        bank: '',
        handed_to: 'Nehasri',
        deposited_on: '2026-08-12',
        deposited_by: 'Meenakshi Ghosh',
        challans: []
      }
    }
  ]
}

const CashRegister = ({ centreId }) => {
  const classes = useStyles()
  const [loading, setLoading] = React.useState(true)
  const [data, setData] = React.useState(null)
  const [error, setError] = React.useState('')
  const [selected, setSelected] = React.useState([])

  // Header filters (user 2026-08-27) — client-side; the ledger is a few dozen
  // rows a month.
  const [qPayer, setQPayer] = React.useState('')
  const [qPhone, setQPhone] = React.useState('')
  const [qCollector, setQCollector] = React.useState('')
  const [fMode, setFMode] = React.useState('all')
  const [fStatus, setFStatus] = React.useState('all')

  // The row being edited, if any: {uuid, amount, reason, note}. Only one at a
  // time — an inline edit that saves on blur is how a wrong number gets
  // committed by a stray click, so it takes an explicit tick.
  const [edit, setEdit] = React.useState(null)
  const [savingRow, setSavingRow] = React.useState(false)

  // Mark-settled modal. The amount is never typed — it is the sum of the
  // ticked rows' RECEIVED amounts, computed again server-side.
  const [open, setOpen] = React.useState(false)
  const [route, setRoute] = React.useState('bank')
  const [bank, setBank] = React.useState('')
  const [handedTo, setHandedTo] = React.useState('')
  const [depositedOn, setDepositedOn] = React.useState(
    Dayjs().format('YYYY-MM-DD')
  )
  const [note, setNote] = React.useState('')
  const [file, setFile] = React.useState(null)
  const [saving, setSaving] = React.useState(false)
  const fileInputRef = React.useRef(null)

  // `silent` refreshes the numbers without tearing the table down into a
  // spinner — used after an inline save, where the row you just edited is the
  // thing you are looking at.
  const load = React.useCallback(
    async ({ silent = false } = {}) => {
      if (!centreId) return
      if (!silent) setLoading(true)
      setError('')
      try {
        setData(await fetchCashLedger(centreId))
      } catch (e) {
        if (import.meta.env.DEV) {
          setData(SAMPLE_LEDGER)
        } else if (!silent) {
          setError('Could not load the cash register. Please retry.')
        }
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [centreId]
  )

  React.useEffect(() => {
    setSelected([])
    setEdit(null)
    load()
  }, [load])

  const rows = data?.results || []
  const kpis = data?.kpis || {}
  const reasons = data?.reasons || []
  const routes = data?.routes || FALLBACK_ROUTES
  // Served, never hardcoded — the day a ₹1,000 note comes back this screen
  // does not need a release.
  const denominations = data?.denominations || FALLBACK_DENOMINATIONS
  const noteTally = data?.note_tally?.rows || []
  // A deposit belongs to one centre, so the cross-centre view is read-only:
  // no tickboxes, no settle, no editing a row from a centre you are only
  // glancing at. Open the centre to work in it.
  const acrossCentres = !!data?.across_centres
  const escalationDays = Number(data?.escalation_days || 3)
  const matches = (hay, q) =>
    String(hay || '')
      .toLowerCase()
      .includes(q.trim().toLowerCase())
  const visible = rows.filter(
    (r) =>
      (!qPayer.trim() || matches(r.payer, qPayer)) &&
      (!qPhone.trim() || matches(r.parent_phone, qPhone)) &&
      (!qCollector.trim() || matches(r.collected_by, qCollector)) &&
      (fMode === 'all' || r.mode === fMode) &&
      (fStatus === 'all' || r.status === fStatus)
  )
  const visibleInHand = visible.filter((r) => r.status === 'in_hand')
  const anyInHand = rows.some((r) => r.status === 'in_hand')
  const isSelected = (r) => selected.includes(r.uuid)
  const toggleSelect = (r) =>
    setSelected((prev) =>
      prev.includes(r.uuid)
        ? prev.filter((u) => u !== r.uuid)
        : [...prev, r.uuid]
    )
  // Select-all follows the FILTERED view, so "filter to cheques → select all"
  // does what it reads as. Already-selected rows outside the filter stay
  // selected; the count and amount below always cover the full selection.
  const toggleSelectAll = () =>
    setSelected((prev) =>
      visibleInHand.every((r) => prev.includes(r.uuid))
        ? prev.filter((u) => !visibleInHand.some((r) => r.uuid === u))
        : [...new Set([...prev, ...visibleInHand.map((r) => r.uuid)])]
    )
  // What the ticked rows add up to, note by note — summed in the browser from
  // counts the server already sent, so ticking a row does not cost a request.
  const selectedTally = React.useMemo(() => {
    const tally = {}
    rows
      .filter((r) => selected.includes(r.uuid))
      .forEach((r) =>
        denominations.forEach((d) => {
          tally[d] =
            (tally[d] || 0) + Number(r.note_count?.counts?.[String(d)] || 0)
        })
      )
    return denominations
      .map((d) => ({
        denomination: d,
        count: tally[d] || 0,
        value: d * (tally[d] || 0)
      }))
      .filter((r) => r.count > 0)
  }, [rows, selected, denominations])

  const selectedAmount = rows
    .filter((r) => isSelected(r))
    .reduce((sum, r) => sum + Number(r.amount_received ?? r.amount ?? 0), 0)

  // ---- inline received editing -------------------------------------------
  const startEdit = (r) => {
    if (!r.can_edit_received || savingRow) return
    setEdit({
      uuid: r.uuid,
      amount: String(r.amount_received ?? r.amount ?? ''),
      reason: r.reason || '',
      note: r.note || '',
      // Blanks rather than zeroes, so eight empty boxes don't read as eight
      // counted nothings.
      counts: toEditable(r.note_count?.counts, denominations)
    })
  }
  const cancelEdit = () => {
    if (savingRow) return
    setEdit(null)
  }
  const editedRow = edit ? rows.find((r) => r.uuid === edit.uuid) : null
  const editVariance =
    edit && editedRow
      ? Number(edit.amount || 0) - Number(editedRow.booked_amount || 0)
      : 0
  const reasonMeta = reasons.find((x) => x.value === edit?.reason)
  const editNeedsReason = !!edit && editVariance !== 0 && !edit.reason
  const editNeedsNote = edit?.reason === 'other' && !edit.note.trim()
  const editAmountValid =
    !!edit && edit.amount !== '' && !Number.isNaN(Number(edit.amount))
  // The count and the amount are one statement made twice. The server refuses
  // them out of step, so the tick refuses them here too rather than letting
  // the user find out from a toast.
  const editCounted = !!edit && anyCounted(edit.counts, denominations)
  const editCountTotal = edit ? noteTotal(edit.counts, denominations) : 0
  const editCountOff =
    editCounted && editAmountValid && editCountTotal !== Number(edit.amount)
  const editBlockedBecause = !edit
    ? ''
    : !editAmountValid
    ? 'Enter the amount that actually came in.'
    : editCountOff
    ? 'The notes and Received have to agree — re-count, or change the amount.'
    : editNeedsReason
    ? 'Pick why it differs from the booked amount.'
    : editNeedsNote
    ? 'Add a short note saying what happened.'
    : ''

  const submitReceived = async () => {
    if (!edit || savingRow) return
    if (!editAmountValid || editNeedsReason || editNeedsNote) return
    if (editCountOff) return
    setSavingRow(true)
    try {
      const saved = await saveCashReceived({
        payInId: edit.uuid,
        amountReceived: edit.amount,
        reason: editVariance === 0 ? '' : edit.reason,
        note: edit.note.trim(),
        counts: toPayload(edit.counts, denominations)
      })
      // Swap the one row in place rather than refetching — the ledger is the
      // screen you are standing on, and a full reload loses scroll position
      // and any ticked rows.
      setData((prev) =>
        prev
          ? {
              ...prev,
              results: prev.results.map((r) =>
                r.uuid === saved.uuid ? saved : r
              )
            }
          : prev
      )
      setEdit(null)
      eventAlert(
        `Received amount saved — ${inr(saved.amount_received)}.`,
        constants.SUCCESS,
        'Cash Register'
      )
      // The KPI strip and the in-hand totals are computed server-side, so pull
      // a fresh copy in the background now the row has moved the numbers.
      load({ silent: true })
    } catch (e) {
      eventAlert(
        e.message || 'Could not save the received amount.',
        constants.ERROR,
        'Cash Register'
      )
    } finally {
      setSavingRow(false)
    }
  }

  // ---- settlement ---------------------------------------------------------
  const closeModal = () => {
    if (saving) return
    setOpen(false)
    setFile(null)
  }

  const routeMeta = routes.find((x) => x.value === route)
  const routeNeedsChallan = routeMeta?.needs_challan ?? route === 'bank'
  const routeNeedsCount = routeMeta?.needs_count ?? route !== 'pre_register'
  const routeNeedsName = route === 'handover' || route === 'hq'
  // Cash cannot be moved on against a figure nobody added up. Named here as
  // well as refused by the server, because finding out from a toast after
  // filling in a challan is the wrong place to learn it.
  const selectedUncounted = rows.filter(
    (r) => selected.includes(r.uuid) && !r.note_count?.counted
  )
  const routeReady =
    (route !== 'bank' || (bank.trim() && file)) &&
    (!routeNeedsName || handedTo.trim()) &&
    (!routeNeedsCount || selectedUncounted.length === 0)

  // "₹46,420 deposited at HDFC Indiranagar." — the confirmation names the
  // destination, so nobody has to reopen the row to see where it went.
  const settledWhere = () => {
    const who = handedTo.trim()
    const where = bank.trim()
    if (route === 'hq') return `sent to HQ Finance${who ? ` — ${who}` : ''}`
    if (route === 'handover') return `handed to ${who}`
    if (route === 'pre_register')
      return where
        ? `recorded as already banked at ${where}`
        : 'recorded as already banked'
    return `deposited at ${where}`
  }

  const submitDeposit = async () => {
    if (!routeReady || saving) return
    setSaving(true)
    try {
      await createCashDeposit({
        centreId,
        route,
        bank: bank.trim(),
        handedTo: handedTo.trim(),
        depositedOn,
        note: note.trim(),
        payInIds: selected,
        file
      })
      eventAlert(
        `${inr(selectedAmount)} ${settledWhere()}.`,
        constants.SUCCESS,
        'Cash Register'
      )
      setOpen(false)
      setFile(null)
      setNote('')
      setHandedTo('')
      setSelected([])
      await load()
    } catch (e) {
      eventAlert(
        e.message || 'Could not record it.',
        constants.ERROR,
        'Cash Register'
      )
      // The server's usual rejection is "already settled / wrong centre —
      // refresh and try again": do the refresh for them, so re-clicking can't
      // loop on stale rows. Selection is cleared because some of it may no
      // longer exist.
      setOpen(false)
      setFile(null)
      setSelected([])
      await load()
    } finally {
      setSaving(false)
    }
  }

  if (loading)
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    )
  if (error)
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
        <Button size="small" onClick={() => load()} style={{ marginTop: 8 }}>
          Retry
        </Button>
      </Box>
    )

  return (
    <Box className={classes.root}>
      <div className={classes.kpis}>
        <div
          className={`${classes.kpi} ${
            kpis.in_hand_amount > 0 ? classes.kpiHot : ''
          }`}
        >
          <div className={classes.kpiValue}>{inr(kpis.in_hand_amount)}</div>
          <div className={classes.kpiLabel}>Cash in hand</div>
        </div>
        <div className={classes.kpi}>
          <div className={classes.kpiValue}>{kpis.in_hand_count || 0}</div>
          <div className={classes.kpiLabel}>Collections in hand</div>
        </div>
        <div
          className={`${classes.kpi} ${
            kpis.oldest_days >= escalationDays ? classes.kpiHot : ''
          }`}
        >
          <div className={classes.kpiValue}>{kpis.oldest_days || 0} days</div>
          <div className={classes.kpiLabel}>Oldest in hand</div>
        </div>
        <div className={classes.kpi}>
          <div className={classes.kpiValue}>
            {inr(kpis.deposited_month_amount)}
          </div>
          <div className={classes.kpiLabel}>
            Settled · {Dayjs().format('MMM')}
          </div>
        </div>
        {/* Money parents still owe on part-paid collections. Nobody is holding
            it, so it is never folded into cash in hand. */}
        {kpis.expected_balance_amount > 0 && (
          <div className={classes.kpi}>
            <div className={classes.kpiValue}>
              {inr(kpis.expected_balance_amount)}
            </div>
            <div className={classes.kpiLabel}>Balance still due</div>
          </div>
        )}
      </div>

      {/* What is physically in the drawer. A bank pay-in slip asks for exactly
          these nine numbers and no total can answer them. */}
      {(noteTally.some((r) => r.count !== 0) || kpis.uncounted_count > 0) && (
        <div className={classes.tallyStrip}>
          <div className={classes.tallyHead}>
            <Typography variant="subtitle2">Notes in hand</Typography>
            {kpis.uncounted_count > 0 && (
              <Typography variant="caption" style={{ color: '#a32b21' }}>
                {kpis.uncounted_count} collection
                {kpis.uncounted_count === 1 ? '' : 's'} not counted yet — they
                cannot be settled until they are.
              </Typography>
            )}
          </div>
          <NoteTally rows={noteTally} />
        </div>
      )}

      <Box className={classes.toolbar}>
        {selected.length > 0 && !acrossCentres ? (
          <>
            <Typography variant="body2" color="textSecondary">
              {selected.length} selected · {inr(selectedAmount)}
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => setOpen(true)}
            >
              Record where it went ({selected.length})
            </Button>
          </>
        ) : (
          <Typography variant="body2" color="textSecondary">
            {acrossCentres
              ? 'Every centre you can see, newest first. Open a single centre to count the notes or settle a deposit.'
              : anyInHand
              ? 'Click a Received amount to count the notes — the total fills itself — then tick the collections you are taking to the bank.'
              : `Nothing in hand — every collection since ${Dayjs(
                  data?.start_date
                ).format('D MMM')} is settled.`}
          </Typography>
        )}
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            {acrossCentres ? (
              <TableCell>Centre</TableCell>
            ) : (
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  disabled={!visibleInHand.length}
                  checked={
                    !!visibleInHand.length &&
                    visibleInHand.every((r) => isSelected(r))
                  }
                  indeterminate={
                    visibleInHand.some((r) => isSelected(r)) &&
                    !visibleInHand.every((r) => isSelected(r))
                  }
                  onChange={toggleSelectAll}
                />
              </TableCell>
            )}
            <TableCell>Collected</TableCell>
            <TableCell>Student / Order</TableCell>
            <TableCell>Parent phone</TableCell>
            <TableCell align="right">Booked</TableCell>
            <TableCell align="right">Received</TableCell>
            <TableCell>Why different</TableCell>
            <TableCell>Mode</TableCell>
            <TableCell>Collected by</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Challan / proof</TableCell>
          </TableRow>
          {/* Filter row (user 2026-08-27): every searchable column filters in
              place; the select-all checkbox above follows what is visible. */}
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell />
            <TableCell>
              <TextField
                size="small"
                placeholder="Search student / order"
                value={qPayer}
                onChange={(e) => setQPayer(e.target.value)}
                fullWidth
              />
            </TableCell>
            <TableCell>
              <TextField
                size="small"
                placeholder="Search phone"
                value={qPhone}
                onChange={(e) => setQPhone(e.target.value)}
                fullWidth
              />
            </TableCell>
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell>
              <TextField
                select
                size="small"
                value={fMode}
                onChange={(e) => setFMode(e.target.value)}
                fullWidth
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="cheque">Cheque</MenuItem>
              </TextField>
            </TableCell>
            <TableCell>
              <TextField
                size="small"
                placeholder="Search name"
                value={qCollector}
                onChange={(e) => setQCollector(e.target.value)}
                fullWidth
              />
            </TableCell>
            <TableCell>
              <TextField
                select
                size="small"
                value={fStatus}
                onChange={(e) => setFStatus(e.target.value)}
                fullWidth
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="in_hand">In hand — still here</MenuItem>
                <MenuItem value="deposited">Settled — left the centre</MenuItem>
              </TextField>
            </TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {visible.map((r) => {
            const old = r.status === 'in_hand' && r.age_days >= escalationDays
            const chip =
              r.status === 'deposited'
                ? {
                    // Every route spelled out. "Banked" on its own said
                    // neither where the money went nor who has it now — which
                    // is the only thing anyone reads this column for.
                    label: `${
                      SETTLED_LABEL[r.deposit?.route] || 'Settled'
                    } ${Dayjs(r.deposit?.deposited_on).format('D MMM')}`,
                    style: CHIP_STYLE.deposited
                  }
                : {
                    label: `In hand · ${r.age_days}d`,
                    style: old ? CHIP_STYLE.in_hand_old : CHIP_STYLE.in_hand
                  }
            const challan = r.deposit?.challans?.[0]
            const editing = edit?.uuid === r.uuid
            // Uncounted cash cannot be settled, so the row carries a marker
            // — one icon on the figure, not a second line under it.
            const needsCount = r.status === 'in_hand' && !r.note_count?.counted
            return (
              <TableRow key={r.uuid} hover>
                {acrossCentres ? (
                  <TableCell>{r.centre || '—'}</TableCell>
                ) : (
                  <TableCell padding="checkbox">
                    {r.status === 'in_hand' ? (
                      <Checkbox
                        size="small"
                        checked={isSelected(r)}
                        onChange={() => toggleSelect(r)}
                      />
                    ) : null}
                  </TableCell>
                )}
                <TableCell>{Dayjs(r.collected_on).format('D MMM')}</TableCell>
                <TableCell>{r.payer}</TableCell>
                <TableCell>{r.parent_phone || '—'}</TableCell>
                <TableCell align="right" className={classes.bookedCell}>
                  {inr(r.booked_amount)}
                </TableCell>
                <TableCell align="right" className={classes.amountCell}>
                  {/* The figure, and nothing under it. What the notes were
                      is a question you ask by clicking, not a second line of
                      grey text on every row of a money column. */}
                  <Tooltip
                    title={
                      acrossCentres
                        ? `Open ${r.centre} to count or correct this`
                        : r.can_edit_received
                        ? r.recorded
                          ? 'Open to check the amount and the notes'
                          : 'Not checked yet — showing the booked amount. Click to record what actually came in.'
                        : 'Already settled — ask Finance to correct it'
                    }
                  >
                    <span
                      className={`${
                        r.can_edit_received && !acrossCentres
                          ? classes.editable
                          : ''
                      } ${r.recorded ? '' : classes.assumed}`}
                      onClick={() => startEdit(r)}
                    >
                      {inr(r.amount_received ?? r.amount)}{' '}
                      {acrossCentres ? null : needsCount ? (
                        <Tooltip title="Not counted — count the notes before this can be settled">
                          <WarningIcon
                            className={classes.warnHint}
                            style={{ fontSize: 14, verticalAlign: 'middle' }}
                          />
                        </Tooltip>
                      ) : (
                        r.can_edit_received && (
                          <EditIcon
                            className={classes.editHint}
                            style={{ fontSize: 13, verticalAlign: 'middle' }}
                          />
                        )
                      )}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {r.variance !== 0 ? (
                    <Tooltip title={r.note || ''}>
                      <Chip
                        size="small"
                        label={`${r.reason_label || 'Difference'} · ${signedInr(
                          r.variance
                        )}`}
                        style={CHIP_STYLE.variance}
                      />
                    </Tooltip>
                  ) : r.recorded ? (
                    <Chip
                      size="small"
                      label="Matches"
                      style={CHIP_STYLE.matches}
                    />
                  ) : (
                    <Typography variant="caption" color="textSecondary">
                      Not checked
                    </Typography>
                  )}
                </TableCell>
                <TableCell style={{ textTransform: 'capitalize' }}>
                  {r.mode}
                </TableCell>
                <TableCell>{r.collected_by || '—'}</TableCell>
                <TableCell>
                  <Chip size="small" label={chip.label} style={chip.style} />
                  {/* Where it actually went — the bank and branch, or the
                      person's name. Shown for every route, because "Deposited
                      16 Aug" without it is the same ambiguity in a new word. */}
                  {!!r.deposit?.destination && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                    >
                      {r.deposit.destination}
                    </Typography>
                  )}
                  {/* Once HQ has opened the envelope the centre can see its
                      count without asking anybody — including a difference
                      they may need to explain. */}
                  {r.hq_receipt && (
                    <Typography
                      variant="caption"
                      display="block"
                      style={{
                        color: r.hq_receipt.difference ? '#a32b21' : '#1b6e52'
                      }}
                    >
                      {r.hq_receipt.difference
                        ? `HQ counted ${inr(r.hq_receipt.counted_total)} · ${
                            r.hq_receipt.reason_label
                          }`
                        : `Counted at HQ · matches`}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {challan ? (
                    <a
                      href={challan.view_url || challan.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {challan.name}
                    </a>
                  ) : (
                    '—'
                  )}
                </TableCell>
              </TableRow>
            )
          })}
          {!rows.length && (
            <TableRow>
              <TableCell colSpan={11}>
                <Typography variant="body2" color="textSecondary">
                  No cash or cheque collections since{' '}
                  {Dayjs(data?.start_date).format('D MMMM YYYY')}. Collections
                  land here automatically when the team uses Orders → Collect
                  with mode Cash or Cheque.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {!!rows.length && !visible.length && (
            <TableRow>
              <TableCell colSpan={11}>
                <Typography variant="body2" color="textSecondary">
                  No rows match the filters.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* One dialog does the whole job: what came in, the notes it came in
          as, and why it differs from the bill. It replaced three inline
          editors squeezed into table cells — a money column should show money
          and nothing else, and the counting needs more room than a cell has. */}
      <Dialog
        open={!!edit}
        onClose={cancelEdit}
        fullWidth
        // Wide enough for the counter's three columns — 500/200/100, 50/20/10,
        // 5/2/1, which is how a person reads a bundle. At `sm` it folds to two
        // and strands the ₹1 on a row of its own.
        PaperProps={{ style: { maxWidth: 760 } }}
      >
        <DialogTitle>
          {editedRow?.payer || 'Collection'}
          <Typography variant="body2" color="textSecondary">
            Booked {inr(editedRow?.booked_amount)} ·{' '}
            {Dayjs(editedRow?.collected_on).format('D MMM')}
            {editedRow?.collected_by ? ` · ${editedRow.collected_by}` : ''}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {edit && (
            <>
              <Typography variant="subtitle2" style={{ marginBottom: 2 }}>
                Count the notes
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                style={{ marginBottom: 12 }}
              >
                The total fills Received. A bank pay-in slip asks for these nine
                numbers, not the total.
              </Typography>
              <NoteCounter
                denominations={denominations}
                counts={edit.counts}
                disabled={savingRow}
                target={editAmountValid ? Number(edit.amount) : null}
                targetLabel="Received"
                onChange={(next) =>
                  setEdit((prev) => ({
                    ...prev,
                    counts: next,
                    // Counting IS recording the receipt: the running total
                    // drives Received rather than waiting to be copied across
                    // by hand. Typing over it afterwards still works — that is
                    // what the mismatch warning is for.
                    amount: String(noteTotal(next, denominations))
                  }))
                }
              />

              <Box
                display="flex"
                flexWrap="wrap"
                alignItems="flex-start"
                gridGap={12}
                style={{ marginTop: 20 }}
              >
                <TextField
                  size="small"
                  type="number"
                  variant="outlined"
                  label="Received"
                  value={edit.amount}
                  disabled={savingRow}
                  error={!editAmountValid || editCountOff}
                  InputLabelProps={{ shrink: true }}
                  style={{ width: 160 }}
                  onChange={(e) => {
                    const next = e.target.value
                    setEdit((prev) => ({ ...prev, amount: next }))
                  }}
                />
                {editVariance !== 0 && (
                  <TextField
                    select
                    size="small"
                    variant="outlined"
                    label={`Why ${signedInr(editVariance)}?`}
                    value={edit.reason}
                    disabled={savingRow}
                    error={editNeedsReason}
                    style={{ minWidth: 280 }}
                    // Without these an empty MUI select floats its label INSIDE
                    // the box, so "Why −₹8,540?" reads as a chosen value and
                    // the greyed-out button looks broken.
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (value) =>
                        value ? (
                          reasons.find((x) => x.value === value)?.label
                        ) : (
                          <span className={classes.placeholder}>
                            Select a reason…
                          </span>
                        )
                    }}
                    onChange={(e) => {
                      const next = e.target.value
                      setEdit((prev) => ({ ...prev, reason: next }))
                    }}
                  >
                    {reasons.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Box>
              {reasonMeta?.sub_label && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                  style={{ marginTop: 4 }}
                >
                  {reasonMeta.sub_label}: {inr(Math.abs(editVariance))}
                </Typography>
              )}
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Note"
                placeholder={
                  edit.reason === 'other'
                    ? 'What happened? (required)'
                    : 'Optional'
                }
                value={edit.note}
                disabled={savingRow}
                error={editNeedsNote}
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 200 }}
                style={{ marginTop: 16 }}
                onChange={(e) => {
                  const next = e.target.value
                  setEdit((prev) => ({ ...prev, note: next }))
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {/* Say WHY the button is off, rather than leaving a greyed control
              and no explanation. */}
          {editBlockedBecause && (
            <Typography
              variant="caption"
              style={{ color: '#c0453f', marginRight: 'auto', paddingLeft: 8 }}
            >
              {editBlockedBecause}
            </Typography>
          )}
          <Button onClick={cancelEdit} disabled={savingRow}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={savingRow || !!editBlockedBecause}
            onClick={submitReceived}
          >
            {savingRow ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={closeModal} maxWidth="xs" fullWidth>
        <DialogTitle>Record where it went</DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ marginBottom: 12 }}>
            {selected.length} collection{selected.length === 1 ? '' : 's'} ·{' '}
            <b>{inr(selectedAmount)}</b> received
          </Typography>
          {/* The bundle you are about to hand over, note by note — copy it
              straight onto the pay-in slip. */}
          {!!selectedTally.length && (
            <div style={{ marginBottom: 12 }}>
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                style={{ marginBottom: 4 }}
              >
                Notes going out
              </Typography>
              <NoteTally rows={selectedTally} />
            </div>
          )}
          {routeNeedsCount && selectedUncounted.length > 0 && (
            <Typography
              variant="body2"
              style={{ marginBottom: 12, color: '#a32b21' }}
            >
              Count the notes first —{' '}
              {selectedUncounted
                .slice(0, 3)
                .map((r) => r.payer.split(' · ')[0])
                .join(', ')}
              {selectedUncounted.length > 3
                ? ` and ${selectedUncounted.length - 3} more`
                : ''}{' '}
              {selectedUncounted.length === 1 ? 'has' : 'have'} no note count.
              Close this and open the Received cell on{' '}
              {selectedUncounted.length === 1 ? 'that row' : 'those rows'}.
            </Typography>
          )}
          <TextField
            select
            label="Where did the cash go?"
            fullWidth
            size="small"
            variant="outlined"
            value={route}
            onChange={(e) => {
              const next = e.target.value
              setRoute(next)
            }}
            style={{ marginBottom: 12 }}
          >
            {routes.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          {routeNeedsName ? (
            <TextField
              label={route === 'hq' ? 'Sent to (at HQ Finance)' : 'Handed to'}
              placeholder="e.g. Nehasri"
              fullWidth
              size="small"
              variant="outlined"
              value={handedTo}
              onChange={(e) => setHandedTo(e.target.value)}
              style={{ marginBottom: 12 }}
            />
          ) : (
            <TextField
              label={
                route === 'bank' ? 'Bank / branch' : 'Bank / branch (optional)'
              }
              placeholder="e.g. HDFC Indiranagar"
              fullWidth
              size="small"
              variant="outlined"
              value={bank}
              onChange={(e) => setBank(e.target.value)}
              style={{ marginBottom: 12 }}
            />
          )}
          <TextField
            label={routeNeedsName ? 'Handed over on' : 'Deposited on'}
            type="date"
            fullWidth
            size="small"
            variant="outlined"
            value={depositedOn}
            onChange={(e) => setDepositedOn(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: Dayjs().format('YYYY-MM-DD') }}
            style={{ marginBottom: 12 }}
          />
          <TextField
            label="Note (optional)"
            fullWidth
            size="small"
            variant="outlined"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ marginBottom: 12 }}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            style={{ display: 'none' }}
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
          >
            {file ? 'Change proof' : 'Attach challan / photo'}
          </Button>
          <Typography
            variant="body2"
            color={file ? 'textPrimary' : 'textSecondary'}
            style={{ marginTop: 6 }}
          >
            {file
              ? file.name
              : routeNeedsChallan
              ? 'Photo or PDF of the bank challan — required.'
              : 'Optional for this route — attach anything you have.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!routeReady || saving}
            onClick={submitDeposit}
          >
            {saving ? 'Recording…' : `Record (${inr(selectedAmount)})`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CashRegister
